import { createFileRoute } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { Button } from '@/components/atoms/button';
import { PublicLayout } from '@/components/public/PublicLayout';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { publicGet, publicPost } from '@/lib/api/publicClient';

type Window = { kind?: string; startAt: string | null; endAt: string | null; date: string | null };
type Move = {
  status: string;
  timezone: string;
  serviceName: string;
  address: string;
  proposed: Window;
  current: Window;
  expiresAt: string;
};
export const Route = createFileRoute('/(public)/schedule-change/$token')({
  component: ScheduleChangePage,
});
export function ScheduleChangePage() {
  const { token } = Route.useParams();
  const qc = useQueryClient();
  const key = ['public-schedule-change', token];
  const path = `/v2/public/scheduling/moves/${encodeURIComponent(token)}`;
  const query = useApiQuery<Move>({ queryKey: key, queryFn: () => publicGet(path), retry: false });
  const accept = useApiMutation<Move>({
    mutationFn: () => publicPost(`${path}/accept`, {}),
    onSuccess: (data) => qc.setQueryData(key, data),
    onError: () => {
      void query.refetch();
    },
  });
  const decline = useApiMutation<Move>({
    mutationFn: () => publicPost(`${path}/decline`, {}),
    onSuccess: (data) => qc.setQueryData(key, data),
    onError: () => {
      void query.refetch();
    },
  });
  const data = query.data;
  const unscheduled = data?.current.kind === 'UNSCHEDULED';
  const format = (window: Window) => {
    if (!window.startAt) return window.date ?? 'Not scheduled';
    const start = DateTime.fromISO(window.startAt).setZone(data!.timezone);
    const end = window.endAt
      ? DateTime.fromISO(window.endAt).setZone(data!.timezone).toFormat('h:mm a')
      : '';
    return `${start.toFormat('cccc, LLLL d · h:mm a')}${end ? ` – ${end}` : ''}`;
  };
  return (
    <PublicLayout maxWidth="sm">
      <div className="space-y-5 rounded-xl border bg-white p-6 text-gray-950">
        <h1 className="text-2xl font-semibold">
          {unscheduled ? 'Appointment offer' : 'Appointment change'}
        </h1>
        {query.isLoading ? (
          <p>Loading your offer…</p>
        ) : query.isError || !data ? (
          <p role="alert">
            This appointment offer is unavailable or has expired. Please contact your service
            provider. Your appointment has not been changed by opening this page.
          </p>
        ) : (
          <>
            <p>
              {data.serviceName}
              <br />
              {data.address}
            </p>
            {data.status === 'ACCEPTED' ? (
              <div role="status">
                <h2 className="font-semibold">Your new arrival window is confirmed</h2>
                <p>{format(data.proposed)}</p>
              </div>
            ) : data.status === 'PENDING' ? (
              <>
                <div>
                  <h2 className="text-sm text-gray-600">
                    {unscheduled ? 'Your request' : 'Your current appointment'}
                  </h2>
                  <p>{format(data.current)}</p>
                </div>
                <div>
                  <h2 className="text-sm text-gray-600">Proposed arrival window</h2>
                  <p className="font-semibold">{format(data.proposed)}</p>
                </div>
                <p className="text-sm">
                  Times in {data.timezone}.{' '}
                  {unscheduled
                    ? 'Your request remains unscheduled until you accept this offer.'
                    : 'Your original appointment stays in place until you accept this change.'}
                </p>
                <Button
                  disabled={accept.isPending || decline.isPending}
                  onClick={() => accept.mutate()}
                >
                  {accept.isPending ? 'Confirming…' : 'Accept new arrival window'}
                </Button>
                <Button
                  variant="outline"
                  disabled={accept.isPending || decline.isPending}
                  onClick={() => decline.mutate()}
                >
                  {unscheduled ? 'Decline offer' : 'Keep my current appointment'}
                </Button>
              </>
            ) : (
              <p role="status">
                {data.status === 'DECLINED'
                  ? unscheduled
                    ? 'You declined this offer. Your request remains unscheduled.'
                    : 'You declined this change. Your original appointment is unchanged.'
                  : 'This offer is no longer active. Please contact your service provider.'}
              </p>
            )}
          </>
        )}
      </div>
    </PublicLayout>
  );
}
